/**
 * Вкладка «Обзор» дашборда склада: выбор недели для плиток, список «Требует
 * внимания» и время среза шапки — чистые функции над ответами четырёх ручек
 * (`/summary`, `/warehouse-turnover/summary`, `/operations`, `/shipments`).
 *
 * Показатели здесь НЕ пересчитываются: плитка берёт значение недели как есть.
 * Единственная арифметика — сумма штук корзин ОСГ по юрлицам/складам для
 * пунктов «просрочка» и «срок 0–10 %» (решение владельца, см.
 * `docs/decisions/warehouse-dashboard.md` → «Обзор»).
 */
import {
  BUCKET_EXPIRED,
  type DashboardSummary,
  isOverdueQueue,
  isStaleQueue,
  LOW_SHELF_LIFE_BUCKETS,
  type Operations,
  type OperationsMetric,
  type OperationsWeek,
  type Shipments,
  STALE_QUEUE_HINT,
} from "@/api/warehouse_dashboard";
import type { TurnoverSummary } from "@/api/warehouse_turnover";
import { extractErrorMessage } from "@/lib/api-errors";
import { ruNum } from "@/lib/format";
import { fmtAge, isoWeekNumber } from "@/lib/warehouse-operations";
import type { DashboardTab } from "@/pages/warehouse-dashboard/tabs";

// --- недели -----------------------------------------------------------------

/** Индексы недель для плитки: `last` — последняя ПОЛНАЯ (`partial=false`),
 *  `prev` — полная перед ней («было»), `current` — текущая неполная. `null` —
 *  такой недели в ответе нет (окно в одну неделю, ответ без текущей). Выбор —
 *  по флагу `partial` из ответа, не по часам браузера. */
export interface WeekPick {
  last: number | null;
  prev: number | null;
  current: number | null;
}

export function pickWeeks(weeks: OperationsWeek[]): WeekPick {
  let last: number | null = null;
  for (let i = weeks.length - 1; i >= 0; i--) {
    if (!weeks[i]!.partial) {
      last = i;
      break;
    }
  }
  const prev = last !== null && last > 0 && !weeks[last - 1]!.partial ? last - 1 : null;
  const tail = weeks.length - 1;
  const current = tail >= 0 && weeks[tail]!.partial ? tail : null;
  return { last, prev, current };
}

const MONTHS_GEN = [
  "января", "февраля", "марта", "апреля", "мая", "июня",
  "июля", "августа", "сентября", "октября", "ноября", "декабря",
];

/** `YYYY-MM-DD` → [день, месяц 0..11] разбором строки, без `new Date` (зона
 *  браузера дату не сдвигает). Неразборчивое — `null`. */
function dayMonth(iso: string): [number, number] | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const month = Number(m[2]) - 1;
  if (month < 0 || month > 11) return null;
  return [Number(m[3]), month];
}

const dd = (n: number) => String(n).padStart(2, "0");

/** Неделя по-русски: `14–20 сентября`, через месяц — `31 августа – 06 сентября`.
 *  Неразборчивые даты — как пришли (это видно). */
export function fmtWeekRu(week: OperationsWeek): string {
  const a = dayMonth(week.start);
  const b = dayMonth(week.end);
  if (!a || !b) return `${week.start}–${week.end}`;
  if (a[1] === b[1]) return `${dd(a[0])}–${dd(b[0])} ${MONTHS_GEN[b[1]]}`;
  return `${dd(a[0])} ${MONTHS_GEN[a[1]]} – ${dd(b[0])} ${MONTHS_GEN[b[1]]}`;
}

/** `нед. 38, 14–20 сентября` — номер ISO-недели (тот же, что в шапке
 *  «Операций», `isoWeekNumber`) и даты по-русски. */
function weekRuWithNumber(week: OperationsWeek): string {
  const n = isoWeekNumber(week.start);
  return n === null ? fmtWeekRu(week) : `нед. ${n}, ${fmtWeekRu(week)}`;
}

/** Строка-период над плитками: какая неделя крупной цифрой, с какой сравнение
 *  и какая идёт. `null` — полной недели в ответе нет (плитки скажут сами). */
export function fmtPeriodLine(weeks: OperationsWeek[]): string | null {
  const { last, prev, current } = pickWeeks(weeks);
  if (last === null) return null;
  const head = weekRuWithNumber(weeks[last]!);
  const parts = [`${head.charAt(0).toUpperCase()}${head.slice(1)} (последняя полная)`];
  if (prev !== null) parts.push(`сравнение с ${weekRuWithNumber(weeks[prev]!)}`);
  if (current !== null) parts.push(`идёт ${weekRuWithNumber(weeks[current]!)}`);
  return parts.join(" · ");
}

/** Показатель «Операций» по ключу; `null` — в ответе его нет (плитка говорит
 *  об этом сама, а не рисует ноль). */
export function findMetric(ops: Operations, key: string): OperationsMetric | null {
  return ops.metrics.find((m) => m.key === key) ?? null;
}

// --- время среза --------------------------------------------------------------

/**
 * Время среза «Обзора» — САМОЕ РАННЕЕ `as_of` из четырёх ответов: картинка не
 * свежее своего самого старого куска. `null`, если хоть один источник упал или
 * ещё грузится — тогда шапка без времени (время неполной картинки выдавало бы
 * её за целую). Неразборчивая метка возвращается как есть — это видно.
 */
export function earliestAsOf(asOfs: (string | null)[]): string | null {
  if (asOfs.length === 0 || asOfs.some((a) => a === null)) return null;
  let best: string | null = null;
  let bestMs = Infinity;
  for (const a of asOfs as string[]) {
    const ms = new Date(a).getTime();
    if (Number.isNaN(ms)) return a;
    if (ms < bestMs) {
      bestMs = ms;
      best = a;
    }
  }
  return best;
}

// --- «Требует внимания» ------------------------------------------------------

/** Состояние источника: данные, ошибка или ни того ни другого (грузится). */
export interface SourceState<T> {
  data: T | undefined;
  error: unknown;
}

export interface OverviewSources {
  summary: SourceState<DashboardSummary>;
  turnover: SourceState<TurnoverSummary>;
  operations: SourceState<Operations>;
  shipments: SourceState<Shipments>;
}

type SourceKey = keyof OverviewSources;

/** Подписи источников для строки «Всё в норме / проверяется». */
export const SOURCE_TITLES: Record<SourceKey, string> = {
  operations: "очереди ФБС (портал)",
  summary: "остатки и сроки годности (1С)",
  turnover: "оборачиваемость (1С)",
  shipments: "отгрузка (1С)",
};

/** Порядок источников в подписи. */
const SOURCE_ORDER: SourceKey[] = ["operations", "summary", "turnover", "shipments"];

/** `critical` — красным (просрочка, упавший источник), `warning` — янтарём
 *  (скоро, неполная картинка, не настроено). */
export type AttentionTone = "critical" | "warning";

export interface AttentionItem {
  key: string;
  tone: AttentionTone;
  text: string;
  /** Уточнение мелко рядом («старейшее 5 ч назад»). */
  hint?: string;
  /** Вкладка с подробностями; `null` — своей вкладки у источника нет
   *  (отгрузка из 1С видна только плиткой «Обзора»). */
  tab: DashboardTab | null;
}

export interface Attention {
  items: AttentionItem[];
  /** Источники, что ответили (по ним «Всё в норме» честно). */
  checked: SourceKey[];
  /** Источники, что ещё грузятся. */
  pending: SourceKey[];
}

/** 409 оборачиваемости = не задана организация в системных настройках (ручка
 *  отдаёт 409 только на это, `warehouse_turnover/router.py`). */
export function isNoOrganizationError(error: unknown): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === 409;
}

export const NO_ORGANIZATION_TEXT =
  "Оборачиваемость: не задана организация — Системные настройки → Общие → технические ссылки 1С";

/**
 * Сумма штук корзин ОСГ по всем юрлицам и складам МП/ОПТ. Ключа нет в каталоге
 * `shelf_life.buckets` — он в `missing`: бэкенд переименовал корзину, и сумма
 * без неё была бы молчаливым нулём.
 */
export function sumBuckets(
  summary: DashboardSummary,
  keys: readonly string[],
): { qty: number; missing: string[] } {
  const known = new Set(summary.shelf_life.buckets.map((b) => b.key));
  const missing = keys.filter((k) => !known.has(k));
  let qty = 0;
  // Скрытые настройкой юрлица — тоже: настройка убирает их блоки с вкладки
  // «Сроки годности», а просрочка на складе от этого не исчезает.
  for (const org of [...summary.shelf_life.organizations, ...summary.shelf_life.hidden]) {
    for (const dir of org.directions) {
      for (const k of keys) qty += dir.buckets[k] ?? 0;
    }
  }
  return { qty, missing };
}

function failed(key: SourceKey, error: unknown, tab: DashboardTab | null): AttentionItem {
  const title = SOURCE_TITLES[key];
  return {
    key: `${key}-error`,
    tone: "critical",
    text: `Не загрузилось: ${title}`,
    hint: extractErrorMessage(error, "ошибка запроса"),
    tab,
  };
}

function warningItems(
  key: SourceKey,
  prefix: string,
  warnings: string[],
  tab: DashboardTab | null,
): AttentionItem[] {
  return warnings.map((w, i) => ({
    key: `${key}-warning-${i}`,
    tone: "warning",
    text: `${prefix}: ${w}`,
    tab,
  }));
}

export function buildAttention(src: OverviewSources): Attention {
  const items: AttentionItem[] = [];
  const checked: SourceKey[] = [];
  const pending: SourceKey[] = [];
  for (const k of SOURCE_ORDER) {
    const s = src[k];
    if (s.error) continue;
    (s.data ? checked : pending).push(k);
  }

  // Порядок пунктов — по срочности: просрочки «сейчас», потом сроки годности,
  // потом неполнота картинки и сбои источников.
  const ops = src.operations;
  if (ops.error) items.push(failed("operations", ops.error, "operations"));
  else if (ops.data) {
    for (const q of ops.data.queues) {
      if (!isOverdueQueue(q.key) || q.count === 0) continue;
      items.push({
        key: `queue-${q.key}`,
        tone: "critical",
        text: `${q.title}: ${ruNum.format(q.count)}`,
        hint: q.oldest_at
          ? `старейшее ${fmtAge(q.oldest_at, ops.data.as_of)} назад`
          : "старейшее: нет даты",
        tab: "operations",
      });
    }
    // Зависшие (без активности > 48 ч) — ОДНОЙ строкой-предупреждением на тип:
    // брошенное не срочно, но копится и прятало живую очередь.
    for (const q of ops.data.queues) {
      if (!isStaleQueue(q.key) || q.count === 0) continue;
      items.push({
        key: `queue-${q.key}`,
        tone: "warning",
        text: `${q.title}: ${ruNum.format(q.count)} — ${STALE_QUEUE_HINT}`,
        hint: q.oldest_at
          ? `старейшая открыта ${fmtAge(q.oldest_at, ops.data.as_of)} назад`
          : "старейшая: нет даты",
        tab: "operations",
      });
    }
  }

  const sum = src.summary;
  if (sum.error) items.push(failed("summary", sum.error, "stock"));
  else if (sum.data) {
    const expired = sumBuckets(sum.data, [BUCKET_EXPIRED]);
    const low = sumBuckets(sum.data, LOW_SHELF_LIFE_BUCKETS);
    const missing = [...expired.missing, ...low.missing];
    if (missing.length > 0) {
      items.push({
        key: "buckets-missing",
        tone: "critical",
        text: `Сроки годности: в ответе нет корзин ${missing.join(", ")} — обзор их не посчитал`,
        tab: "shelf-life",
      });
    }
    if (expired.qty > 0) {
      items.push({
        key: "expired",
        tone: "critical",
        text: `Просрочка на складах МП/ОПТ: ${ruNum.format(expired.qty)} шт`,
        tab: "shelf-life",
      });
    }
    if (low.qty > 0) {
      items.push({
        key: "low-shelf-life",
        tone: "warning",
        text: `Остаток со сроком 0–10 %: ${ruNum.format(low.qty)} шт`,
        tab: "shelf-life",
      });
    }
    items.push(...warningItems("summary", "Остатки", sum.data.warnings, "stock"));
  }

  const tv = src.turnover;
  if (tv.error) {
    items.push(
      isNoOrganizationError(tv.error)
        ? { key: "turnover-no-org", tone: "warning", text: NO_ORGANIZATION_TEXT, tab: "turnover" }
        : failed("turnover", tv.error, "turnover"),
    );
  } else if (tv.data) {
    items.push(...warningItems("turnover", "Оборачиваемость", tv.data.warnings, "turnover"));
  }

  const sh = src.shipments;
  if (sh.error) items.push(failed("shipments", sh.error, null));
  else if (sh.data) items.push(...warningItems("shipments", "Отгрузка", sh.data.warnings, null));

  return { items, checked, pending };
}

// --- товар у контрагентов ---------------------------------------------------

/** Информационная строка «Обзора» о товаре у контрагентов (комиссия,
 *  переработка): НЕ предупреждение — ожидаемое исключение из оборачиваемости.
 *  `null` — сейчас у контрагентов ничего нет (или ответа ещё нет). */
export function counterpartyNote(tv: TurnoverSummary | undefined): string | null {
  if (!tv || (tv.counterparty_stock_qty === 0 && tv.counterparty_stock_sum === 0)) return null;
  return (
    `Товар у контрагентов (комиссия, переработка): ${ruNum.format(tv.counterparty_stock_qty)} шт ` +
    `на ${fmtRubShort(tv.counterparty_stock_sum)} — не входит в оборачиваемость склада`
  );
}

// --- форматирование плиток ---------------------------------------------------

const ruInt = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });
const ruMln = new Intl.NumberFormat("ru-RU", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Рубли крупной цифрой плитки: от миллиона — `12,3 млн ₽`, меньше — целые.
 *  Точное значение — в `title` плитки (`fmtRubExact`). */
export function fmtRubShort(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${ruMln.format(value / 1_000_000)} млн ₽`;
  return `${ruInt.format(value)} ₽`;
}

export function fmtRubExact(value: number): string {
  return `${ruInt.format(value)} ₽`;
}
